import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '../../../environments/environment';
import { storageKey } from './utils.service';
import { clearAuthStorage } from './auth-storage';

@Injectable({ providedIn: 'root' })
export class AuthService {
  private http = inject(HttpClient);
  private apiUrl = environment.apiUrl;

  login(body: { email: string; password: string; keepMeLogin?: boolean }): Observable<any> {
    // Credentials so the HttpOnly trusted-browser cookie (two-step
    // verification) is sent and can be updated.
    return this.http.post(`${this.apiUrl}auth/login`, body, { withCredentials: true });
  }

  signup(body: any): Observable<any> {
    return this.http.post(`${this.apiUrl}user/adduser`, body);
  }

  forgetPassword(body: { email: string }): Observable<any> {
    return this.http.post(`${this.apiUrl}user/forgotPassword`, body);
  }

  // The backend's change-password endpoint (unchanged from legacy) only ever
  // reads body.forgotPwdHash to look up the user — it never received the token
  // under `hashId`/`activationCode`, so every real reset silently failed to
  // find the account even though the email link and token itself were valid.
  resetPassword(body: { password: string; hashId?: string }): Observable<any> {
    return this.http.post(`${this.apiUrl}user/change-password`, {
      password: body.password,
      forgotPwdHash: body.hashId,
    });
  }

  setPassword(body: { password: string; activationCode: string }): Observable<any> {
    return this.http.post(`${this.apiUrl}user/change-password`, {
      password: body.password,
      forgotPwdHash: body.activationCode,
    });
  }

  refreshToken(): Observable<any> {
    const token = localStorage.getItem(storageKey.refreshToken);
    return this.http.post(`${this.apiUrl}auth/refresh-token`, { refresh_token: token });
  }

  registerMO(body: any): Observable<any> {
    return this.http.post(`${this.apiUrl}auth/mslogin`, body);
  }

  activationCode(body: any): Observable<any> {
    return this.http.post(`${this.apiUrl}user/adduser`, body);
  }

  isAuthenticated(): boolean {
    const token = localStorage.getItem(storageKey.accessToken);
    if (!token) return false;
    return !this.isTokenExpired(token);
  }

  // Client-side expiry check only (a UX nicety, not a security boundary — the
  // backend still verifies the token's signature on every request regardless).
  // Lets the guard redirect to login immediately for a known-expired token
  // instead of letting the user briefly land on a page whose API calls then
  // fail with 401s.
  private isTokenExpired(token: string): boolean {
    try {
      const payload = JSON.parse(atob(token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/')));
      if (!payload?.exp) return false;
      return payload.exp * 1000 <= Date.now();
    } catch {
      return true;
    }
  }

  isAdmin(): boolean {
    return localStorage.getItem(storageKey.isAdmin) === 'true';
  }

  /** Signs out: tells the server to end this browser's session (best effort,
   *  so signing out never waits on or fails because of the network) and clears
   *  local storage. */
  logout(): void {
    const refreshToken = localStorage.getItem(storageKey.refreshToken);
    if (refreshToken) {
      this.http
        .post(`${this.apiUrl}auth/logout`, { refresh_token: refreshToken })
        .subscribe({ error: () => undefined });
    }
    clearAuthStorage();
  }
}
